import * as Sentry from "@sentry/capacitor";
import type { IntlConfig, IntlError } from "use-intl";

/**
 * How loudly a missing or broken message is surfaced.
 *
 * - `test`: every error throws, `ENVIRONMENT_FALLBACK` included, so a
 *   missing key or a date formatted without a time zone fails the test that
 *   rendered it.
 * - `development`: logged, so the screen keeps rendering while it is fixed.
 * - `production`: reported once per code and message, never thrown. A host
 *   sees the key path in place of the words, not a crash.
 */
export type IntlPolicyMode = "development" | "production" | "test";

export interface IntlPolicy {
  getMessageFallback: NonNullable<IntlConfig["getMessageFallback"]>;
  onError: NonNullable<IntlConfig["onError"]>;
}

export function intlPolicyMode(): IntlPolicyMode {
  if (import.meta.env.MODE === "test") {
    return "test";
  }
  return import.meta.env.DEV ? "development" : "production";
}

/**
 * The key path, never an English template. use-intl calls this without the
 * message's values, so an English string with a placeholder would render its
 * raw ICU (`Automatic ({language})`), which reads worse than a key.
 */
function keyPathFallback({
  key,
  namespace,
}: {
  key: string;
  namespace?: string;
}): string {
  return namespace ? `${namespace}.${key}` : key;
}

function throwIntlError(error: IntlError): never {
  throw error;
}

function logIntlError(error: IntlError): void {
  console.error(error);
}

/**
 * Reports each distinct error once. use-intl puts the key path and locale in
 * a missing message's text, so one missing key is one report however many
 * times it renders, and a production build names the key without its
 * placeholder values.
 */
function reportIntlErrorOnce(): (error: IntlError) => void {
  const reported = new Set<string>();
  return (error) => {
    const { code, originalMessage = "" } = error;
    const reportKey = `${code}|${originalMessage}`;
    if (reported.has(reportKey)) {
      return;
    }
    reported.add(reportKey);
    Sentry.captureException(error, {
      fingerprint: ["interface-intl", code, originalMessage],
      tags: { area: "i18n", intlErrorCode: code },
    });
  };
}

const ON_ERROR_BY_MODE: Record<IntlPolicyMode, () => IntlPolicy["onError"]> = {
  development: () => logIntlError,
  production: reportIntlErrorOnce,
  test: () => throwIntlError,
};

/**
 * The `onError` and `getMessageFallback` an `IntlProvider` takes. Create it
 * once per provider and keep it: both are provider props, and a new function
 * on each render re-creates every translator below it.
 */
export function createIntlPolicy(mode: IntlPolicyMode): IntlPolicy {
  return {
    getMessageFallback: keyPathFallback,
    onError: ON_ERROR_BY_MODE[mode](),
  };
}
